'use client';

import { useEffect, useState } from 'react';
import { Box, HStack, Portal, Text } from '@chakra-ui/react';
import { Check, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Menu } from '@chakra-ui/react';
import {
  RECOMMENDED_RESEARCH_MODEL_ID,
  type PublicResearchModel,
} from '@/lib/research-assistant/models';
import {
  getStoredResearchReasoningEffort,
  RESEARCH_REASONING_EFFORT_EVENT,
  RESEARCH_REASONING_EFFORT_STORAGE_KEY,
  resolveSupportedResearchReasoningEffort,
  type ResearchReasoningEffort,
} from '@/lib/research-assistant/reasoning-effort';

const EFFORT_LABELS: Record<ResearchReasoningEffort, string> = {
  none: 'Off',
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Maximum',
};

interface ModelSelectorProps {
  models: PublicResearchModel[];
  selectedModel: PublicResearchModel | null;
  selectedModelId: string;
  disabled?: boolean;
  isLoading?: boolean;
  reasoningEffort?: ResearchReasoningEffort;
  onChange: (modelId: string) => void;
  onReasoningEffortChange?: (value: ResearchReasoningEffort) => void;
}

function SettingsRowContent({ label, value }: { label: string; value: string }) {
  return (
    <HStack w="full" minH="32px" px="2" justify="space-between" gap="3" color="ui.text.primary">
      <Text fontSize="13px" fontWeight="550" letterSpacing="-0.01em">
        {label}
      </Text>
      <HStack gap="1" color="ui.text.tertiary" minW="0">
        <Text fontSize="13px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
          {value}
        </Text>
        <ChevronRight size={14} />
      </HStack>
    </HStack>
  );
}

const COMPACT_MENU_CONTENT_PROPS = {
  layerStyle: 'surfacePopover',
  p: '1',
  borderRadius: 'glassCard',
  borderColor: 'ui.border.subtle',
  boxShadow: 'var(--attune-shadow-floating, none)',
} as const;

const COMPACT_MENU_ITEM_PROPS = {
  minH: '32px',
  h: '32px',
  px: '2',
  py: '0',
  borderRadius: 'glassInput',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '550',
  letterSpacing: '-0.01em',
  _hover: { bg: 'ui.surface.rowHover', color: 'ui.text.primary' },
  _focusVisible: {
    bg: 'ui.surface.selected',
    color: 'ui.text.primary',
    outline: '2px solid',
    outlineColor: 'ui.border.accent',
    outlineOffset: '-2px',
  },
} as const;

export function ModelSelector({
  models,
  selectedModel,
  selectedModelId,
  disabled,
  isLoading,
  reasoningEffort: controlledReasoningEffort,
  onChange,
  onReasoningEffortChange,
}: ModelSelectorProps) {
  const current = selectedModel || models.find(model => model.id === selectedModelId) || models[0];
  const triggerDisabled = disabled || isLoading || models.length === 0;
  const [storedReasoningEffort, setStoredReasoningEffort] =
    useState<ResearchReasoningEffort>('medium');
  const availableReasoningEfforts = current?.reasoningEfforts || [];
  const reasoningEffort = resolveSupportedResearchReasoningEffort(
    availableReasoningEfforts,
    controlledReasoningEffort || storedReasoningEffort
  );

  useEffect(() => {
    setStoredReasoningEffort(getStoredResearchReasoningEffort());
  }, []);

  const updateReasoningEffort = (value: ResearchReasoningEffort) => {
    setStoredReasoningEffort(value);
    window.localStorage.setItem(RESEARCH_REASONING_EFFORT_STORAGE_KEY, value);
    window.dispatchEvent(new CustomEvent(RESEARCH_REASONING_EFFORT_EVENT, { detail: { value } }));
    onReasoningEffortChange?.(value);
  };

  return (
    <Menu.Root positioning={{ placement: 'top-start', gutter: 6 }}>
      <Menu.Trigger asChild disabled={triggerDisabled}>
        <Box
          as="button"
          data-composer-control-trigger
          display="inline-flex"
          alignItems="center"
          gap="1.5"
          minH="26px"
          maxW={{ base: '152px', sm: '184px' }}
          px="2"
          borderRadius="full"
          color="ui.text.secondary"
          cursor={triggerDisabled ? 'not-allowed' : 'pointer'}
          opacity={triggerDisabled ? 0.55 : 1}
          _hover={triggerDisabled ? {} : { bg: 'ui.surface.selected', color: 'ui.text.primary' }}
          _focusVisible={{
            outline: '2px solid',
            outlineColor: 'accent.solid',
            outlineOffset: '2px',
          }}
          aria-label={`Model: ${current?.label || 'loading'}`}
          title={`Model: ${current?.label || 'loading'}`}
        >
          <Sparkles size={12} />
          <Text
            as="span"
            data-composer-control-label
            fontSize="xs"
            fontWeight="650"
            color="ui.text.primary"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {current?.shortLabel || 'Model'}
          </Text>
          <ChevronDown data-composer-control-chevron size={12} />
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content {...COMPACT_MENU_CONTENT_PROPS} w="220px" maxW="calc(100vw - 24px)">
            <Menu.Root positioning={{ placement: 'right-start', gutter: 4 }}>
              <Menu.TriggerItem {...COMPACT_MENU_ITEM_PROPS} p="0">
                <SettingsRowContent label="Model" value={current?.shortLabel || 'Choose'} />
              </Menu.TriggerItem>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content
                    {...COMPACT_MENU_CONTENT_PROPS}
                    w="244px"
                    maxW="calc(100vw - 24px)"
                    maxH="304px"
                    overflowY="auto"
                  >
                    {models.map(model => {
                      const isSelected = model.id === current?.id;
                      const isRecommended = model.id === RECOMMENDED_RESEARCH_MODEL_ID;
                      return (
                        <Menu.Item
                          {...COMPACT_MENU_ITEM_PROPS}
                          key={model.id}
                          value={model.id}
                          disabled={!model.available}
                          onSelect={() => {
                            if (model.available) onChange(model.id);
                          }}
                          cursor={model.available ? 'pointer' : 'not-allowed'}
                          opacity={model.available ? 1 : 0.4}
                          title={model.disabledReason}
                        >
                          <HStack w="full" minW="0" gap="2">
                            <Text
                              flex="1"
                              minW="0"
                              color="ui.text.primary"
                              fontWeight={isSelected ? '650' : '550'}
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {model.shortLabel}
                            </Text>
                            {isRecommended ? (
                              <Text
                                color="ui.text.tertiary"
                                fontSize="10px"
                                fontWeight="700"
                                letterSpacing="0.04em"
                                textTransform="uppercase"
                                whiteSpace="nowrap"
                              >
                                Recommended
                              </Text>
                            ) : null}
                            {model.currentOnly ? (
                              <Text
                                color="ui.text.tertiary"
                                fontSize="10px"
                                fontWeight="700"
                                letterSpacing="0.04em"
                                textTransform="uppercase"
                                whiteSpace="nowrap"
                              >
                                Current
                              </Text>
                            ) : null}
                            {isSelected ? (
                              <Box color="ui.text.secondary" flexShrink={0}>
                                <Check size={14} />
                              </Box>
                            ) : null}
                          </HStack>
                        </Menu.Item>
                      );
                    })}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>

            {reasoningEffort ? (
              <Menu.Root positioning={{ placement: 'right-start', gutter: 4 }}>
                <Menu.TriggerItem {...COMPACT_MENU_ITEM_PROPS} p="0">
                  <SettingsRowContent label="Effort" value={EFFORT_LABELS[reasoningEffort]} />
                </Menu.TriggerItem>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content {...COMPACT_MENU_CONTENT_PROPS} w="180px">
                      {availableReasoningEfforts.map(effort => (
                        <Menu.Item
                          {...COMPACT_MENU_ITEM_PROPS}
                          key={effort}
                          value={`effort-${effort}`}
                          onSelect={() => updateReasoningEffort(effort)}
                        >
                          <HStack w="full" minW="0" gap="2">
                            <Text flex="1" color="ui.text.primary">
                              {EFFORT_LABELS[effort]}
                            </Text>
                            {effort === reasoningEffort ? (
                              <Box color="ui.text.secondary" flexShrink={0}>
                                <Check size={14} />
                              </Box>
                            ) : null}
                          </HStack>
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            ) : null}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
